import {
	CartErrorEvent,
	CartLinesUpdateEvent,
	ProductSelectEvent,
	createViewEventElement,
	type CartAjaxResponse,
} from '@theme/standard-events'
import type { Cart, ProductVariant } from '@/scripts/shopify'

export type StandardCartContext = 'product' | 'cart' | 'dialog'
export type StandardCartLine =
	| {
			action: 'add'
			merchandiseId: string | number
			quantity: number
	  }
	| {
			action: 'update' | 'remove'
			id: string | number
			quantity: number
	  }

type CartMutationBoundary = {
	deferred: ReturnType<typeof CartLinesUpdateEvent.createPromise>
	settled: boolean
	target: EventTarget
}

function errorMessage(error: unknown) {
	if (error instanceof Error) return error.message
	if (typeof error === 'string') return error
	return window.cartStrings.error
}

export function beginStandardCartMutation(
	target: EventTarget,
	context: StandardCartContext,
	lines: StandardCartLine[]
): CartMutationBoundary {
	if (lines.length === 0)
		throw new Error('A standard cart mutation requires at least one line')
	const action = lines[0].action
	if (!lines.every((line) => line.action === action)) {
		throw new Error('A standard cart event can contain only one action')
	}

	const deferred = CartLinesUpdateEvent.createPromise()
	// Attach a rejection observer so a valid error event does not become an unhandled rejection
	// when no app is currently observing the standard event.
	void deferred.promise.catch(() => undefined)

	const payload =
		action === 'add'
			? {
					action,
					context,
					lines: lines.map((line) => {
						if (line.action !== 'add')
							throw new Error('Invalid add line')
						return {
							merchandiseId: line.merchandiseId,
							quantity: line.quantity,
						}
					}),
					promise: deferred.promise,
				}
			: {
					action,
					context,
					lines: lines.map((line) => {
						if (line.action === 'add')
							throw new Error('Invalid cart line')
						return { id: line.id, quantity: line.quantity }
					}),
					promise: deferred.promise,
				}

	target.dispatchEvent(new CartLinesUpdateEvent(payload))
	return { deferred, settled: false, target }
}

export async function resolveStandardCartMutation(
	boundary: CartMutationBoundary,
	ajaxCart?: Cart
) {
	if (boundary.settled) return
	try {
		const cart =
			ajaxCart ??
			(await fetch(window.routes.cart_url + '.js', {
				headers: { Accept: 'application/json' },
			}).then(async (response) => {
				if (!response.ok)
					throw new Error(`Cart refresh failed (${response.status})`)
				return (await response.json()) as Cart
			}))
		boundary.deferred.resolve({
			cart: CartLinesUpdateEvent.createCartFromAjaxResponse(
				cart as unknown as CartAjaxResponse
			),
		})
		boundary.settled = true
	} catch (error) {
		rejectStandardCartMutation(boundary, error, 'SERVICE_UNAVAILABLE')
	}
}

export function rejectStandardCartMutation(
	boundary: CartMutationBoundary,
	error: unknown,
	code: ConstructorParameters<typeof CartErrorEvent>[0]['code'] = 'INVALID'
) {
	if (boundary.settled) return
	boundary.settled = true
	const message = errorMessage(error)
	boundary.target.dispatchEvent(new CartErrorEvent({ error: message, code }))
	boundary.deferred.reject(
		error instanceof Error ? error : new Error(message)
	)
}

type ProductSelectionBoundary = {
	deferred: ReturnType<typeof ProductSelectEvent.createPromise>
	optionNames: string[]
	selectedOptions: string[]
}

export function beginStandardProductSelection(
	target: HTMLElement,
	selectedOptions: string[]
): ProductSelectionBoundary {
	const optionNames = JSON.parse(
		target.dataset.optionNames ?? '[]'
	) as string[]
	const deferred = ProductSelectEvent.createPromise()
	void deferred.promise.catch(() => undefined)
	target.dispatchEvent(
		new ProductSelectEvent({
			product: {
				id: target.dataset.productId ?? '',
				title: target.dataset.productTitle ?? '',
				handle: target.dataset.productHandle ?? '',
			},
			selectedOptions: selectedOptions.map((value, index) => ({
				name: optionNames[index] ?? `Option ${index + 1}`,
				value,
			})),
			promise: deferred.promise,
		})
	)
	return { deferred, optionNames, selectedOptions }
}

export function resolveStandardProductSelection(
	boundary: ProductSelectionBoundary,
	variant?: ProductVariant
) {
	boundary.deferred.resolve({
		variant: variant
			? {
					id: variant.id,
					title: variant.title,
					availableForSale: variant.available,
					price: {
						amount: (variant.price / 100).toFixed(2),
						currencyCode: window.Shopify?.currency?.active ?? 'USD',
					},
					selectedOptions: boundary.selectedOptions.map(
						(value, index) => ({
							name:
								boundary.optionNames[index] ??
								`Option ${index + 1}`,
							value,
						})
					),
				}
			: null,
	})
}

export const StandardViewEventElement = createViewEventElement()
